import { useState, FormEvent } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { StockItem } from '../../types';
import { createScrap } from '../../services/storage';
import { Scissors, Package, Ruler, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useToast } from '../../contexts/ToastContext';

interface ScrapGenerationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessToken: string;
  parentItem: StockItem | null;
  onSuccess: () => void;
}

export function ScrapGenerationDialog({
  open,
  onOpenChange,
  accessToken,
  parentItem,
  onSuccess,
}: ScrapGenerationDialogProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [relatedOrderId, setRelatedOrderId] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!parentItem) return;

    const widthNum = parseFloat(width);
    const heightNum = parseFloat(height);
    const quantityNum = parseInt(quantity);

    // Validation
    if (!widthNum || widthNum <= 0) {
      toast.error('Informe uma largura válida');
      return;
    }
    if (!heightNum || heightNum <= 0) {
      toast.error('Informe uma altura válida');
      return;
    }
    if (!quantityNum || quantityNum <= 0) {
      toast.error('Informe uma quantidade válida');
      return;
    }

    // Check if scrap dimensions exceed parent dimensions
    if (parentItem.width && widthNum > parentItem.width) {
      toast.error(`Largura do retalho (${widthNum}mm) não pode ser maior que a do material de origem (${parentItem.width}mm)`);
      return;
    }
    if (parentItem.height && heightNum > parentItem.height) {
      toast.error(`Altura do retalho (${heightNum}mm) não pode ser maior que a do material de origem (${parentItem.height}mm)`);
      return;
    }

    setSaving(true);

    try {
      await createScrap(
        accessToken,
        parentItem.id,
        widthNum,
        heightNum,
        quantityNum,
        relatedOrderId.trim() || undefined
      );

      toast.success(`Retalho gerado com sucesso a partir de ${parentItem.name}`);
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setWidth('');
    setHeight('');
    setQuantity('1');
    setRelatedOrderId('');
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  if (!parentItem) return null;

  // Calculate area percentage if both dimensions are available
  const widthNum = parseFloat(width) || 0;
  const heightNum = parseFloat(height) || 0;
  const scrapArea = widthNum * heightNum;
  const parentArea = (parentItem.width || 0) * (parentItem.height || 0);
  const areaPercentage = parentArea > 0 ? ((scrapArea / parentArea) * 100).toFixed(1) : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5" />
            Gerar Retalho
          </DialogTitle>
          <DialogDescription>
            Crie um retalho a partir do material de origem. O retalho será vinculado automaticamente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Parent Item Info */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Package className="h-4 w-4" />
              Material de Origem
            </div>
            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nome:</span>
                <span className="font-medium">{parentItem.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">SKU:</span>
                <span className="font-mono text-xs">{parentItem.skuCode}</span>
              </div>
              {parentItem.width && parentItem.height && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dimensões:</span>
                  <span>
                    {parentItem.width} x {parentItem.height}
                    {parentItem.thickness && ` x ${parentItem.thickness}`} mm
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estoque:</span>
                <span>{parentItem.quantity} {parentItem.unit || 'un'}</span>
              </div>
            </div>
          </div>

          {/* Scrap Dimensions */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Ruler className="h-4 w-4" />
              Dimensões do Retalho
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="scrap-width">Largura (mm) *</Label>
                <Input
                  id="scrap-width"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max={parentItem.width || undefined}
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder={parentItem.width ? `Max: ${parentItem.width}` : ''}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="scrap-height">Altura (mm) *</Label>
                <Input
                  id="scrap-height"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max={parentItem.height || undefined}
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder={parentItem.height ? `Max: ${parentItem.height}` : ''}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="scrap-quantity">Quantidade *</Label>
                <Input
                  id="scrap-quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Area calculation feedback */}
            {areaPercentage && parseFloat(areaPercentage) > 0 && (
              <div className={cn(
                "text-sm p-2 rounded-md flex items-center gap-2",
                parseFloat(areaPercentage) > 50 ? "bg-yellow-50 text-yellow-800" : "bg-blue-50 text-blue-800"
              )}>
                {parseFloat(areaPercentage) > 50 && <AlertTriangle className="h-4 w-4" />}
                <span>
                  Área do retalho: {scrapArea.toLocaleString('pt-BR')} mm² ({areaPercentage}% do material original)
                </span>
              </div>
            )}
          </div>

          {/* Optional: Related Order */}
          <div className="space-y-2">
            <Label htmlFor="related-order">Pedido Relacionado (opcional)</Label>
            <Input
              id="related-order"
              value={relatedOrderId}
              onChange={(e) => setRelatedOrderId(e.target.value)}
              placeholder="Ex: MT-2026-0123"
            />
            <p className="text-xs text-muted-foreground">
              Se o retalho foi gerado a partir de um corte para um pedido específico
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              <Scissors className="h-4 w-4 mr-1" />
              {saving ? 'Gerando...' : 'Gerar Retalho'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
